import {
  customerMarketShopfrontApi,
  Site,
  marketApi
} from '@envato/foundation-types'
import { ActionCreator } from 'react-redux'
import { ThunkAction } from 'redux-thunk'

import { IRootState } from '../../common/reducers'
import { AlertType, IAlertPayload } from '../../typings/alert'
import { IPageSeoMeta } from '../../typings/seoMetadata'
import axiosVerbose from '../utils/axios/axiosVerbose'
import axiosCached from '../utils/axios/axiosCached'
import { logError } from '../utils/errors'
import { decodeMetaString } from '../utils/formatString'
import { initToggledPanels } from './layout'
import { ISetMenuConfigAction } from './server'
import { getUser } from './user'
import { initClientUserSearchResultsViewType } from './user/preference'

export enum TypeKeys {
  FETCH_CATEGORIES_WITH_CHILDREN_REQUEST = 'FETCH_CATEGORIES_WITH_CHILDREN_REQUEST',
  FETCH_CATEGORIES_WITH_CHILDREN_SUCCESS = 'FETCH_CATEGORIES_WITH_CHILDREN_SUCCESS',
  FETCH_CATEGORIES_WITH_CHILDREN_FAILURE = 'FETCH_CATEGORIES_WITH_CHILDREN_FAILURE',
  GET_STATS_SUCCESS = 'GET_STATS_SUCCESS',
  GET_STATS_FAIL = 'GET_STATS_FAIL',
  SET_CURRENT_SITE = 'SET_CURRENT_SITE',
  SET_BASE_URL = 'SET_BASE_URL',
  SET_MENU_CONFIG = 'SET_MENU_CONFIG',
  SHOW_ALERT = 'SHOW_ALERT',
  HIDE_ALERT = 'HIDE_ALERT',
  RESET_ALERT = 'RESET_ALERT',
  SET_ERROR_PAGE = 'SET_ERROR_PAGE',
  RESET_ERROR_PAGE = 'RESET_ERROR_PAGE',
  NO_INDEX = 'NO_INDEX',
  SET_CLIENT_VERSION = 'SET_CLIENT_VERSION',
  SET_CLIENT_FINISHED_LOADING = 'SET_CLIENT_FINISHED_LOADING',
  SET_PRODUCT_IMPRESSIONS = 'SET_PRODUCT_IMPRESSIONS',
  SET_SEO_META = 'SET_SEO_META',
  SET_CANONICAL_PATH = 'SET_CANONICAL_PATH'
}

export interface IFetchCategoriesWithChildrenRequest {
  type: TypeKeys.FETCH_CATEGORIES_WITH_CHILDREN_REQUEST
}

export interface IFetchCategoriesWithChildrenSuccess {
  type: TypeKeys.FETCH_CATEGORIES_WITH_CHILDREN_SUCCESS
  payload: marketApi.ICategory[]
}

export interface IFetchCategoriesWithChildrenFailure {
  type: TypeKeys.FETCH_CATEGORIES_WITH_CHILDREN_FAILURE
}

export interface IGetStatsSuccessAction {
  type: TypeKeys.GET_STATS_SUCCESS
  payload: Readonly<customerMarketShopfrontApi.stats.IResult>
}

export interface IGetStatsFailAction {
  type: TypeKeys.GET_STATS_FAIL
  error: boolean
}

export interface ISetCurrentSiteAction {
  type: TypeKeys.SET_CURRENT_SITE
  payload: Site
}

export interface ISetBaseUrlAction {
  type: TypeKeys.SET_BASE_URL
  payload: string
}

export interface IShowAlertAction {
  type: TypeKeys.SHOW_ALERT
  payload: Readonly<IAlertPayload>
}

export interface IHideAlertAction {
  type: TypeKeys.HIDE_ALERT
  payload: Readonly<{ message: string }>
}

export interface IResetAlertAction {
  type: TypeKeys.RESET_ALERT
}

export interface ISetErrorPage {
  type: TypeKeys.SET_ERROR_PAGE
  payload: Readonly<{ id: any }>
}

export interface IResetErrorPage {
  type: TypeKeys.RESET_ERROR_PAGE
}

export interface INoIndexAction {
  type: TypeKeys.NO_INDEX
}

export interface ISetClientFinishedLoadingAction {
  type: TypeKeys.SET_CLIENT_FINISHED_LOADING
}

export interface ISetSeoMetaAction {
  type: TypeKeys.SET_SEO_META
  payload: Readonly<IPageSeoMeta>
}

export interface ISetCanonicalPathAction {
  type: TypeKeys.SET_CANONICAL_PATH
  payload: Readonly<string>
}

export type ActionTypes =
  | IFetchCategoriesWithChildrenRequest
  | IFetchCategoriesWithChildrenSuccess
  | IFetchCategoriesWithChildrenFailure
  | IGetStatsSuccessAction
  | IGetStatsFailAction
  | ISetCurrentSiteAction
  | ISetBaseUrlAction
  | ISetMenuConfigAction
  | IShowAlertAction
  | IHideAlertAction
  | IResetAlertAction
  | ISetErrorPage
  | IResetErrorPage
  | INoIndexAction
  | ISetClientFinishedLoadingAction
  | ISetCanonicalPathAction
  | ISetSeoMetaAction

let CACHED_STATS: customerMarketShopfrontApi.stats.IResult | null = null

export const fetchStats = ({
  apiAppUrlPrefix
}: {
  apiAppUrlPrefix: string
}): ThunkAction<
  Promise<IGetStatsSuccessAction | IGetStatsFailAction>,
  IRootState,
  void
> => {
  return (dispatch, getState) => {
    const state = getState()
    if (state.common.stats) {
      const action: IGetStatsSuccessAction = {
        type: TypeKeys.GET_STATS_SUCCESS,
        payload: state.common.stats
      }
      return Promise.resolve(dispatch(action))
    }

    if (CACHED_STATS) {
      const action: IGetStatsSuccessAction = {
        type: TypeKeys.GET_STATS_SUCCESS,
        payload: CACHED_STATS
      }
      return Promise.resolve(dispatch(action))
    }

    return axiosVerbose
      .get<customerMarketShopfrontApi.stats.IResult>(
        `${apiAppUrlPrefix}/shopfront-api/stats`
      )
      .then(response => {
        CACHED_STATS = response.data

        const action: IGetStatsSuccessAction = {
          type: TypeKeys.GET_STATS_SUCCESS,
          payload: CACHED_STATS
        }

        return dispatch(action)
      })
      .catch(err => Promise.reject(logError(err)))
      .catch(() => {
        const action: IGetStatsFailAction = {
          type: TypeKeys.GET_STATS_FAIL,
          error: true
        }
        return dispatch(action)
      })
  }
}

export const fetchCategoriesWithChildrenRequest = (): IFetchCategoriesWithChildrenRequest => ({
  type: TypeKeys.FETCH_CATEGORIES_WITH_CHILDREN_REQUEST
})

export const fetchCategoriesWithChildrenFailure = (): IFetchCategoriesWithChildrenFailure => ({
  type: TypeKeys.FETCH_CATEGORIES_WITH_CHILDREN_FAILURE
})

export const fetchCategoriesWithChildrenSuccess = (
  categories: marketApi.ICategory[]
): IFetchCategoriesWithChildrenSuccess => ({
  type: TypeKeys.FETCH_CATEGORIES_WITH_CHILDREN_SUCCESS,
  payload: categories
})

export const fetchCategoriesWithChildren = ({
  currentSite,
  apiAppUrlPrefix
}: {
  currentSite: Site
  apiAppUrlPrefix: string
}): ThunkAction<Promise<any>, IRootState, void> => (dispatch, getState) => {
  dispatch(fetchCategoriesWithChildrenRequest())
  return axiosCached
    .get<customerMarketShopfrontApi.categoriesWithChildren.IResult>(
      `${apiAppUrlPrefix}/shopfront-api/categories_with_children/${currentSite}`
    )
    .then(response =>
      dispatch(fetchCategoriesWithChildrenSuccess(response.data.data))
    )
    .catch(err => Promise.reject(logError(err)))
    .catch(() => dispatch(fetchCategoriesWithChildrenFailure()))
}

export const setCurrentSite: ActionCreator<
  ThunkAction<ISetCurrentSiteAction, IRootState, void>
> = (currentSite: Site) => {
  return (dispatch, getState) => {
    dispatch({
      type: 'loadSiteConfigs',
      payload: {
        currentSite
      }
    })

    const action: ISetCurrentSiteAction = {
      type: TypeKeys.SET_CURRENT_SITE,
      payload: currentSite
    }
    return dispatch(action)
  }
}

export const setBaseUrl = (baseUrl: string) => {
  const action: ISetBaseUrlAction = {
    type: TypeKeys.SET_BASE_URL,
    payload: baseUrl
  }
  return action
}

export const showAlert = (
  message: string,
  type: AlertType = AlertType.warning,
  canBeDismissed = true
) => {
  const action: IShowAlertAction = {
    type: TypeKeys.SHOW_ALERT,
    payload: {
      message,
      type,
      canBeDismissed
    }
  }
  return action
}

export const hideAlert = (message: string) => {
  const action: IHideAlertAction = {
    type: TypeKeys.HIDE_ALERT,
    payload: {
      message
    }
  }
  return action
}

export const resetAlerts = () => {
  const action: IResetAlertAction = {
    type: TypeKeys.RESET_ALERT
  }
  return action
}

export const setErrorPage = (id: any) => {
  const action: ISetErrorPage = {
    type: TypeKeys.SET_ERROR_PAGE,
    payload: {
      id
    }
  }
  return action
}

export const resetErrorPage = () => {
  const action: IResetErrorPage = {
    type: TypeKeys.RESET_ERROR_PAGE
  }
  return action
}

export const noIndex = () => {
  const action: INoIndexAction = {
    type: TypeKeys.NO_INDEX
  }
  return action
}

export const clientFinishedLoading = (): ThunkAction<any, IRootState, void> => {
  return (dispatch, getState) => {
    const { location } = getState().router
    dispatch(initClientUserSearchResultsViewType(location))
    dispatch(getUser())
    dispatch(initToggledPanels())
  }
}

export const setCanonicalPath = (path: string) => {
  const action: ISetCanonicalPathAction = {
    type: TypeKeys.SET_CANONICAL_PATH,
    payload: path
  }
  return action
}

export const setSeoMeta = ({
  title,
  meta_description,
  description
}: {
  title: string
  meta_description?: string
  description: string
}) => {
  const desc = meta_description || description
  const action: ISetSeoMetaAction = {
    type: TypeKeys.SET_SEO_META,
    payload: {
      title: decodeMetaString(title),
      meta_description: decodeMetaString(desc)
    }
  }
  return action
}
